/**
 * This file exists to avoid circular dependencies due to the `APP_STORE` import
 * If we can remove APP_STORE we can merge this code back into `base.ts`
 **/
import { BaseQueryFn } from '@reduxjs/toolkit/dist/query';
import axios, { AxiosRequestConfig, AxiosError, isCancel } from 'axios';
import Cookies from 'js-cookie';
import { APP_STORE } from '../../store/store';
import { SUBS_SCHEMA } from '../../../config/auth';
// import { signOut } from '../authActions';

export function objectToQueryString(obj) {
  const params = new URLSearchParams();

  for (const key in obj) {
    // see https://eslint.org/docs/latest/rules/no-prototype-builtins
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      params.append(key, obj[key]);
    }
  }

  return params.toString();
}
// NOTE - Token Renewal logic is not added here as no call is being made to looker through these hooks as of today
// avoid circular dependencies by lazily creating the axios instance
const createAxiosInstance = () => {
  const apiAxiosWithAuth = axios.create({
    withCredentials: true,
    headers: {
      'Content-type': 'application/json',
    },
  });

  let isRefreshing = false;
  let subscribers: ((token: string) => void)[] = [];

  const subscribeTokenRefresh = (cb: (token: string) => void) => {
    subscribers.push(cb);
  };

  const onRefreshed = (token: string) => {
    subscribers.forEach((cb) => cb(token));
    subscribers = [];
  };

  const refreshAccessToken = async (): Promise<string> => {
    if (!isRefreshing) {
      isRefreshing = true;
      try {
        const res = await fetch('/api/get-access-token', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        const { user_auth_token } = await res.json();
        if (process.env.NEXT_PUBLIC_HOST_URL === 'http://localhost:3000') {
          document.cookie =
            'user_auth_token=' +
            user_auth_token +
            '; expires=' +
            new Date(Date.now() + 3300000).toUTCString() +
            '; path=/; SameSite=Strict;';
        } else {
          document.cookie = `user_auth_token=${user_auth_token}; expires=${new Date(
            Date.now() + 3300000
          ).toUTCString()}; path=/; domain=quattr.com; SameSite=Strict; Secure`;
        }
        isRefreshing = false;
        onRefreshed(user_auth_token);
        return user_auth_token;
      } catch (error) {
        console.error('Error | refreshAccessToken', error);
        throw error;
      }
    }

    return new Promise((resolve) => {
      subscribeTokenRefresh((token: string) => {
        resolve(token);
      });
    });
  };

  apiAxiosWithAuth.interceptors.request.use(async (config) => {
    if (!config.headers) {
      config.headers = new axios.AxiosHeaders();
    }

    let looker_auth_token = Cookies.get('user_auth_token') || '';
    if (!looker_auth_token || looker_auth_token === 'undefined') {
      looker_auth_token = await refreshAccessToken();
    }

    config.headers['auth_token'] = looker_auth_token;

    // AUTH0_TODO Q2022-3777 - Temporary Solution to send sub token to BE, this token should be added in user session
    const subAuthToken = localStorage.getItem('subscriber-auth-token');
    if (subAuthToken) {
      config.headers['subscriber-auth-token'] = subAuthToken;
    }

    const store = APP_STORE;
    if (store.getState().root.customerInfo && store.getState().root.customerInfo.length && subAuthToken) {
      const custInfo = store.getState().root.customerInfo.find((i) => i.cust_code === SUBS_SCHEMA);
      if (custInfo?.additional_user_info?.isDemoUser && custInfo?.additional_user_info?.replaceKeywords) {
        config.headers['isdemouser'] = true;
        config.headers['sandbox-env-replacements'] = btoa(
          JSON.stringify(custInfo?.additional_user_info?.replaceKeywords)
        );
      }
    } else {
      config.headers['checkforsandboxenv'] = true;
    }

    // TODO check after Q2022-3235 is in production, if this `if` statement can be removed:
    if (config.url?.startsWith(process.env.NEXT_PUBLIC_QUATTR_API_URL as string)) {
      let queryString = '';
      if (config.params) {
        queryString = objectToQueryString(config.params);
        config.params = {};
      }
      config.url = `${process.env.NEXT_PUBLIC_HOST_URL}/api/qwa-backend?url=${encodeURIComponent(
        `${config.url}${queryString ? `?${queryString}` : ``}`
      )}`;
    }

    config.headers['Content-Type'] = 'application/json';
    return config;
  });

  apiAxiosWithAuth.interceptors.response.use(undefined, async (error) => {
    if (error.response?.status === 401) {
      try {
        const { signOut } = await import('../authActions');
        await signOut();
        return Promise.reject(error);
      } catch (signOutError) {
        return Promise.reject(signOutError);
      }
    }
    return Promise.reject(error);
  });

  return apiAxiosWithAuth;
};

type AxiosRequestArgs = {
  url: string;
  method: AxiosRequestConfig['method'];
  data?: AxiosRequestConfig['data'];
  params?: AxiosRequestConfig['params'];
  signal?: AbortSignal;
};

// see https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#axios-basequery
export const axiosBaseQueryWithAuth = (
  { baseUrl }: { baseUrl: string } = { baseUrl: '' }
): BaseQueryFn<AxiosRequestArgs, unknown, unknown> => {
  const apiAxiosWithAuth = createAxiosInstance();

  return async ({ url, method, data, params }, { signal }) => {
    try {
      const result = await apiAxiosWithAuth({
        url: baseUrl + url,
        method,
        data,
        params,
        signal,
      });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      if (axios.isAxiosError(axiosError)) {
        if (axios.isCancel(axiosError)) {
          console.warn('Axios request cancelled', axiosError.message);
          return {
            error: {
              status: 'ABORTED',
              data: null,
            },
          };
        }

        return {
          error: {
            status: err.response?.status || 500,
            data: err.response?.data || err.message,
          },
        };
      }

      return {
        error: {
          status: 500,
          data: 'Unknown error occurred',
        },
      };
    }
  };
};
